import { ChangeDetectorRef, Directive } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject } from 'rxjs';
import {
  MajorCategories,
  CTScan,
  AdvancedSpecializedImaging,
  BodyScan,
  DigitalXRay,
  FunctionalPreventiveImaging,
  InterventionalRadiology,
  MRI,
  NuclearMedicine,
  PETScan,
  Ultrasound,
  XRay,
} from '@alpha/types';
import {
  Checkbox,
  IFilterItem,
  FilterType,
  RadioButton,
  ICardGroupItem,
} from 'app-core';
import { UIUtilService } from 'app-service';

@Directive()
export class ScanClinicSearchFilter {
  public cards: Array<ICardGroupItem> = [];
  public _filterItems: Array<IFilterItem> = [];
  public checkboxFilter: FilterType = 'Checkbox';
  public readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  public categoryItems: Array<RadioButton> = [
    {
      label: `${MajorCategories[MajorCategories.BodyScan]} / Clinic`,
      value: MajorCategories.BodyScan,
      checked: true,
      selected: true,
    },
  ];
  public advancedSpecializedImagingItems: Array<Checkbox> = Object.keys(
    AdvancedSpecializedImaging,
  )
    .filter(
      (key) =>
        typeof AdvancedSpecializedImaging[
          key as keyof typeof AdvancedSpecializedImaging
        ] === 'string',
    )
    .map((key: any) => ({
      label: AdvancedSpecializedImaging[key as any].replace(/_/g, ' '),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)) as Checkbox[];
  public cTScanItems: Array<Checkbox> = Object.keys(CTScan)
    .filter((key) => typeof CTScan[key as keyof typeof CTScan] === 'string')
    .map((key: any) => ({
      label: CTScan[key as any].replace(/_/g, ' '),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)) as Checkbox[];
  public digitalXRayItems: Array<Checkbox> = Object.keys(DigitalXRay)
    .filter(
      (key) => typeof DigitalXRay[key as keyof typeof DigitalXRay] === 'string',
    )
    .map((key: any) => ({
      label: DigitalXRay[key as any].replace(/_/g, ' '),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)) as Checkbox[];
  public functionalPreventiveImagingItems: Array<Checkbox> = Object.keys(
    FunctionalPreventiveImaging,
  )
    .filter(
      (key) =>
        typeof FunctionalPreventiveImaging[
          key as keyof typeof FunctionalPreventiveImaging
        ] === 'string',
    )
    .map((key: any) => ({
      label: FunctionalPreventiveImaging[key as any].replace(/_/g, ' '),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)) as Checkbox[];
  public interventionalRadiologyItems: Array<Checkbox> = Object.keys(
    InterventionalRadiology,
  )
    .filter(
      (key) =>
        typeof InterventionalRadiology[
          key as keyof typeof InterventionalRadiology
        ] === 'string',
    )
    .map((key: any) => ({
      label: InterventionalRadiology[key as any].replace(/_/g, ' '),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)) as Checkbox[];
  public mRIItems: Array<Checkbox> = Object.keys(MRI)
    .filter((key) => typeof MRI[key as keyof typeof MRI] === 'string')
    .map((key: any) => ({
      label: MRI[key as any].replace(/_/g, ' '),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)) as Checkbox[];
  public nuclearMedicineItems: Array<Checkbox> = Object.keys(NuclearMedicine)
    .filter(
      (key) =>
        typeof NuclearMedicine[key as keyof typeof NuclearMedicine] ===
        'string',
    )
    .map((key: any) => ({
      label: NuclearMedicine[key as any].replace(/_/g, ' '),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)) as Checkbox[];
  public pETScanItems: Array<Checkbox> = Object.keys(PETScan)
    .filter((key) => typeof PETScan[key as keyof typeof PETScan] === 'string')
    .map((key: any) => ({
      label: PETScan[key as any].replace(/_/g, ' '),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)) as Checkbox[];
  public ultrasoundItems: Array<Checkbox> = Object.keys(Ultrasound)
    .filter(
      (key) => typeof Ultrasound[key as keyof typeof Ultrasound] === 'string',
    )
    .map((key: any) => ({
      label: Ultrasound[key as any].replace(/_/g, ' '),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)) as Checkbox[];

  public xRayItems: Array<Checkbox> = Object.keys(XRay)
    .filter((key) => typeof XRay[key as keyof typeof XRay] === 'string')
    .map((key: any) => ({
      label: XRay[key as any].replace(/_/g, ' '),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)) as Checkbox[];
  private _currentQueryParams: Params = {};

  public constructor(
    private readonly activatedRoute: ActivatedRoute,
    public readonly _cdRef: ChangeDetectorRef,
    private readonly router: Router,
    public readonly _uiUtilService: UIUtilService,
  ) {
    this._addFilters();
  }

  public onItemSelected(
    item: Checkbox,
    category: BodyScan,
    items: Array<number>,
  ): void {
    this.cards = [];
    switch (category) {
      case BodyScan.AdvancedSpecializedImaging:
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { ai: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      case BodyScan.CTScan:
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { cs: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      case BodyScan.DigitalXRay:
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { dx: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      case BodyScan.FunctionalPreventiveImaging:
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { fi: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      case BodyScan.InterventionalRadiology:
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { ir: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      case BodyScan.MRI:
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { mi: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      case BodyScan.NuclearMedicine:
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { nm: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      case BodyScan.PETScan:
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { psn: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      case BodyScan.Ultrasound:
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { ud: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      case BodyScan.XRay:
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { xy: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      default:
        break;
    }
  }

  public get filterItems(): Array<IFilterItem> {
    return this._filterItems;
  }

  public set filterItems(newFilterItems: Array<IFilterItem>) {
    this._filterItems = newFilterItems;
  }

  public get currentQueryParams(): Params {
    return this._currentQueryParams;
  }

  public set currentQueryParams(newFilterItems: Params) {
    this._currentQueryParams = newFilterItems;
  }

  public _addFilters(): void {
    this.filterItems.push(
      {
        title: 'Advanced Specialized Imaging',
        filters: {
          list: () => this.advancedSpecializedImagingItems,
          type: this.checkboxFilter,
        },
        onItemSelected: (item: any) => {
          const selectedItems = this.advancedSpecializedImagingItems!.filter(
            (item) => item!.checked,
          ).map((item) => item!.value);
          this.onItemSelected(
            item,
            BodyScan.AdvancedSpecializedImaging,
            selectedItems,
          );
        },
      },
      {
        title: 'CT Scan',
        filters: {
          list: () => this.cTScanItems,
          type: this.checkboxFilter,
        },
        onItemSelected: (item: any) => {
          const selectedItems = this.cTScanItems!.filter(
            (item) => item!.checked,
          ).map((item) => item!.value);
          this.onItemSelected(item, BodyScan.CTScan, selectedItems);
        },
      },
      {
        title: 'Digital XRay',
        filters: {
          list: () => this.digitalXRayItems,
          type: this.checkboxFilter,
        },
        onItemSelected: (item: any) => {
          const selectedItems = this.digitalXRayItems!.filter(
            (item) => item!.checked,
          ).map((item) => item!.value);
          this.onItemSelected(item, BodyScan.DigitalXRay, selectedItems);
        },
      },
      {
        title: 'Functional Preventive Imaging',
        filters: {
          list: () => this.functionalPreventiveImagingItems,
          type: this.checkboxFilter,
        },
        onItemSelected: (item: any) => {
          const selectedItems = this.functionalPreventiveImagingItems!.filter(
            (item) => item!.checked,
          ).map((item) => item!.value);
          this.onItemSelected(
            item,
            BodyScan.FunctionalPreventiveImaging,
            selectedItems,
          );
        },
      },
      {
        title: 'Interventional Radiology',
        filters: {
          list: () => this.interventionalRadiologyItems,
          type: this.checkboxFilter,
        },
        onItemSelected: (item: any) => {
          const selectedItems = this.interventionalRadiologyItems!.filter(
            (item) => item!.checked,
          ).map((item) => item!.value);
          this.onItemSelected(
            item,
            BodyScan.InterventionalRadiology,
            selectedItems,
          );
        },
      },
      {
        title: 'MRI',
        filters: {
          list: () => this.mRIItems,
          type: this.checkboxFilter,
        },
        onItemSelected: (item: any) => {
          const selectedItems = this.mRIItems!.filter(
            (item) => item!.checked,
          ).map((item) => item!.value);
          this.onItemSelected(item, BodyScan.MRI, selectedItems);
        },
      },
      {
        title: 'Nuclear Medicine',
        filters: {
          list: () => this.nuclearMedicineItems,
          type: this.checkboxFilter,
        },
        onItemSelected: (item: any) => {
          const selectedItems = this.nuclearMedicineItems!.filter(
            (item) => item!.checked,
          ).map((item) => item!.value);
          this.onItemSelected(item, BodyScan.NuclearMedicine, selectedItems);
        },
      },
      {
        title: 'PET Scan',
        filters: {
          list: () => this.pETScanItems,
          type: this.checkboxFilter,
        },
        onItemSelected: (item: any) => {
          const selectedItems = this.pETScanItems!.filter(
            (item) => item!.checked,
          ).map((item) => item!.value);
          this.onItemSelected(item, BodyScan.PETScan, selectedItems);
        },
      },
      {
        title: 'Ultrasound',
        filters: {
          list: () => this.ultrasoundItems,
          type: this.checkboxFilter,
        },
        onItemSelected: (item: any) => {
          const selectedItems = this.ultrasoundItems!.filter(
            (item) => item!.checked,
          ).map((item) => item!.value);
          this.onItemSelected(item, BodyScan.Ultrasound, selectedItems);
        },
      },
      {
        title: 'XRay',
        filters: {
          list: () => this.xRayItems,
          type: this.checkboxFilter,
        },
        onItemSelected: (item: any) => {
          const selectedItems = this.xRayItems!.filter(
            (item) => item!.checked,
          ).map((item) => item!.value);
          this.onItemSelected(item, BodyScan.XRay, selectedItems);
        },
      },
    );
  }
}
